import { compare } from '@shared/helpers/compare.helpers';
import { omit, replaceProperties } from '@shared/helpers/object.helpers';
import { Shape, Storey } from '@shared/models/design/storey.model';
import { ItemOrItemId, SemiPartial } from '@shared/models/type-helpers.model';
import { designGuards } from '../design-typeguards.helpers';
import { getStoreyById } from '../storey.helpers';
import {
  hasUnresolvedMezzanineStorey,
  raiseMezzanineParentHeights,
  syncMezzanineStoreysAfterUpdate,
} from '../mezzanine-helpers';
import { PointMove } from './design-mutator.models';
import { mutateStorey } from './storey.mutator';
import { ArraySplice } from 'type-fest';
import { shallowEqualMap } from '@shared/helpers/array.helpers';
import { Point2D } from '@shared/models/geometry.model';
import { removeStoreysFromList } from './storeys/remove-storeys.helpers';
import { getShapeByIdFromStorey } from '../storey-shape.helpers';
import { createStorey } from '../factories/storey.factory';

/**
 * Apply updates to a list of storeys
 * @param storeys - The list of storeys to update
 * @param updates - The updates to apply to the storeys
 * @returns The updated list of storeys (if any changes were made)
 */
export const updateStoreys = (
  storeys: Storey[],
  ...updates: SemiPartial<Storey, 'id'>[]
): Storey[] => {
  const updatedStoreys = storeys.map((s) => {
    const update = updates.find((u) => u.id === s.id);
    if (!update) return s;
    return replaceProperties(s, update);
  });
  const withParents = raiseMezzanineParentHeights(updatedStoreys);
  const synced = syncMezzanineStoreysAfterUpdate(withParents, updates);
  // Leave the list unchanged when a deck still cannot match its parent.
  if (hasUnresolvedMezzanineStorey(synced)) return storeys;
  return compare.equal.shallow(synced, storeys) ? storeys : synced;
};

/**
 * Add a new storey to the project geometry by cloning the top storey (if it exists)
 * @param design - The project geometry
 * @returns The new project geometry
 */
const addStorey = (storeys: Storey[]): Storey[] => {
  // Get the top non-mezzanine storey
  const nonMezzanineStoreys = storeys.filter(
    designGuards.is.nonMezzanineStorey,
  );
  const topStorey = nonMezzanineStoreys[nonMezzanineStoreys.length - 1];
  const newStorey = createStorey(
    topStorey ? omit(topStorey, 'name', 'elevation') : undefined,
  );

  // Preserve previous behavior: if no base storey existed, ensure ground floor elevation = 0.
  return [...storeys, topStorey ? newStorey : { ...newStorey, elevation: 0 }];
};

/**
 * Move multiple points to new positions
 * @param design
 * @param moveOperations
 * @returns
 */
const moveManyPoints = (
  storeys: Storey[],
  ...moveOperations: PointMove[]
): Storey[] => {
  return shallowEqualMap(storeys, (storey) => {
    for (const moveOperation of moveOperations) {
      const { shapeId, pointIndex: index, position } = moveOperation;
      const shape = getShapeByIdFromStorey(storey, shapeId);
      if (!shape) continue; // If shape doesn't exist in this storey continue to next move operation
      storey = mutateStorey.shape.point.move(storey, shapeId, index, position);
    }
    return storey;
  });
};

const removeManyShapes = (
  storeys: Storey[],
  ...shapeOrIds: ItemOrItemId<Shape>[]
): Storey[] => {
  return shallowEqualMap(storeys, (storey) => {
    return mutateStorey.shape.remove(storey, ...shapeOrIds);
  });
};

const updateManyShapes = (
  storeys: Storey[],
  ...updates: SemiPartial<Shape, 'id'>[]
): Storey[] => {
  return shallowEqualMap(storeys, (storey) => {
    return mutateStorey.shape.update(storey, ...updates);
  });
};

/**
 * Wrap a storey mutator function to mutate a list of storeys
 * @param mutatorFn - The mutator function to use
 * @returns A function that mutates a storey in a list of storeys
 */
const storeyMutatorFnFactory = <
  T extends (storey: Storey, ...args: any[]) => Storey,
>(
  mutatorFn: T,
): ((
  storeys: Storey[],
  storeyOrId: ItemOrItemId<Storey>,
  ...args: ArraySplice<Parameters<T>, 0, 1>
) => Storey[]) => {
  return (storeys, storeyOrId, ...args) => {
    const storey = getStoreyById(storeys, storeyOrId);
    if (!storey) return storeys;
    return shallowEqualMap(storeys, (storey) => mutatorFn(storey, ...args));
  };
};

const translateAllPoints = (storeys: Storey[], delta: Point2D): Storey[] => {
  return shallowEqualMap(storeys, (storey) => {
    return mutateStorey.all.points.translate(storey, delta);
  });
};

/**
 *
 */
export const mutateStoreys = {
  update: updateStoreys,
  add: addStorey,
  remove: removeStoreysFromList,
  shape: {
    add: storeyMutatorFnFactory(mutateStorey.shape.add),
    remove: storeyMutatorFnFactory(mutateStorey.shape.remove),
    update: storeyMutatorFnFactory(mutateStorey.shape.update),
    point: {
      move: storeyMutatorFnFactory(mutateStorey.shape.point.move),
      remove: storeyMutatorFnFactory(mutateStorey.shape.point.remove),
      insertAt: storeyMutatorFnFactory(mutateStorey.shape.point.insertAt),
      set: storeyMutatorFnFactory(mutateStorey.shape.point.set),
      add: storeyMutatorFnFactory(mutateStorey.shape.point.add),
      translateMany: storeyMutatorFnFactory(
        mutateStorey.shape.point.translateMany,
      ),
    },
  },
  many: {
    shapes: {
      update: updateManyShapes,
      remove: removeManyShapes,
    },
    points: {
      move: moveManyPoints,
    },
  },
  all: {
    points: {
      translate: translateAllPoints,
    },
  },
} as const;
